
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { callFunction } from '@/integrations/supabase/functions';
import { criticalLogger } from '@/utils/logger';

export interface GlobalSearchResult {
  prayers: any[];
  verses: any[];
  topics: { slug: string; name: string; }[];
  trending: { reference: string; combined_score: number; source_type: 'external'|'internal' }[];
}

export function useGlobalSearch(query: string) {
  const [results, setResults] = useState<GlobalSearchResult>({
    prayers: [],
    verses: [],
    topics: [],
    trending: []
  });
  const [loading, setLoading] = useState(false);

  const fetchGlobal = useCallback(async () => {
    if (!query.trim()) {
      setResults({ prayers: [], verses: [], topics: [], trending: [] });
      return;
    }
    setLoading(true);

    try {
      // 1) Supabase global-search edge function
      const { data: gs, error: ge } = await callFunction('global-search', {
        body: { action: 'search', query, limit: 10 }
      });

      if (ge) {
        criticalLogger.error('Global search error:', ge);
      }

      // 2) API.Bible topics endpoint via edge function
      let topics: any[] = [];
      try {
        const { data: topicData, error: topicError } = await callFunction('get-topics');
        
        if (topicError) {
          criticalLogger.error('Topics fetch error:', topicError);
        } else if ((topicData as any)?.data) {
          topics = (topicData as any).data;
        }
      } catch (err) {
        criticalLogger.error('Topics fetch error', err);
      }

      // 3) Trending reads via RPC
      const { data: trending, error: te } = await supabase.rpc('get_globally_popular_verses', {
        p_limit: 5
      });

      if (te) {
        criticalLogger.error('Trending verses error:', te);
      }

      // Transform trending data to match expected interface
      const transformedTrending = trending?.map((item: any) => ({
        reference: item.reference,
        combined_score: item.combined_score || 0,
        source_type: item.source_type || 'external'
      })) || [];

      setResults({
        prayers: (gs as any)?.prayers ?? [],
        verses: (gs as any)?.verses ?? [],
        topics: topics.map((t: any) => ({ slug: t.slug, name: t.name })),
        trending: transformedTrending
      });
    } catch (error) {
      criticalLogger.error('Global search fetch error:', error);
      setResults({ prayers: [], verses: [], topics: [], trending: [] });
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchGlobal();
  }, [query, fetchGlobal]);

  return { ...results, loading };
}
